import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { CirclePlus, RefreshCw, Unlink } from 'lucide-react';
import MaterialComponentParent from './MaterialComponentParent';
import {
  collectUniqueMaterialsInObject,
  findMaterialListAssetRoot,
  removeMaterialFromLibrary,
  selectionMeshesShareSingleMaterial,
  storeMaterialInLibrary,
  syncChildMaterialOverridesToAssetRoot,
} from '../../utils/ThreeJsUtils/materialPropertyEditor';
import {
  captureMeshMaterialBindings,
} from '../../utils/ThreeJsUtils/materialMaps';
import { MeshMaterialBindingCommand } from '../../utils/Command';
import undoRedoManager from '../../utils/UndoRedoManager';
import store from '../../redux/store';
import {
  selected_object,
  setMaterialHighlightMeshes,
  updateSceneMeshMaterialSettings,
} from '../../redux/Slice/gridSlice';
import { setChildSelectionMode } from '../../pages/Dashboard/GridPage';
import { clearMultiSelection } from '../../utils/selectAllObjects';
import { syncMaterialHighlightVisuals } from '../../utils/ThreeJsUtils/selectionOutline';
import MaterialSpherePreview from './MaterialSpherePreview';
import { GridDropdown } from '../../utils/gridUtils';
import {
  applyRenderView,
  getObjectRenderView,
} from '../gridpagefunction/materialFunction';

/** Viewport preview modes for Render View (material list / GLB stay unchanged). */
export const RENDER_VIEW_OPTIONS = [
  { value: 'None', label: 'None' },
  { value: 'Opaque', label: 'Opaque' },
  { value: 'Cutout', label: 'Cutout' },
  { value: 'Fade', label: 'Fade' },
  { value: 'Wireframe', label: 'Wireframe' },
];
/** @deprecated */
export const RENDERER_TYPE_OPTIONS = RENDER_VIEW_OPTIONS;

/** Stored / applied when nothing is selected in the dropdown. */
const DEFAULT_RENDER_VIEW = 'None';

/**
 * Normalize stored Render View.
 * Empty / Original → None (no overlay).
 */
function normalizeRenderViewValue(value) {
  if (value == null) return DEFAULT_RENDER_VIEW;
  if (typeof value !== 'string' || !value.trim()) return DEFAULT_RENDER_VIEW;
  if (value === 'Original') return DEFAULT_RENDER_VIEW;
  const allowed = RENDER_VIEW_OPTIONS.some((opt) => opt.value === value);
  return allowed ? value : DEFAULT_RENDER_VIEW;
}

/**
 * Value for GridDropdown.
 * None shows as empty ("Select") so it doesn't look chosen by default,
 * but "None" stays in the menu so a mode can be cleared after selecting one.
 */
function renderViewDropdownValue(value) {
  const normalized = normalizeRenderViewValue(value);
  return normalized === DEFAULT_RENDER_VIEW ? '' : normalized;
}

const getSelectionId = (obj) =>
  obj ? (obj.uuid ?? obj._id ?? obj.id ?? obj.name ?? null) : null;

function findMaterialIdForObject(materials, object) {
  if (!object || !materials?.length) return null;

  if (object.isMesh || object.isSkinnedMesh) {
    const mats = Array.isArray(object.material)
      ? object.material
      : [object.material];
    const hit = materials.find((item) =>
      mats.some((m) => m && m.uuid === item.materialUuid)
    );
    return hit?.id || null;
  }

  const multi = store.getState()?.assets_object?.selectedMeshesForGrouping || [];
  if (multi.length > 1 && selectionMeshesShareSingleMaterial(multi)) {
    return findMaterialIdForObject(materials, multi[0]);
  }

  return null;
}

function nextMaterialName(existingMaterials) {
  const used = new Set(
    (existingMaterials || []).map((m) => String(m.name || '').toLowerCase())
  );
  if (!used.has('material')) return 'Material';
  let i = 1;
  while (used.has(`material.${String(i).padStart(3, '0')}`)) i += 1;
  return `Material.${String(i).padStart(3, '0')}`;
}

function findFirstMesh(root) {
  if (!root) return null;
  if (root.isMesh || root.isSkinnedMesh) return root;
  let found = null;
  root.traverse?.((obj) => {
    if (found) return;
    if (obj.isMesh || obj.isSkinnedMesh) found = obj;
  });
  return found;
}

function isUnderAsset(obj, assetRoot) {
  if (!obj || !assetRoot) return false;
  let current = obj;
  while (current) {
    if (current === assetRoot) return true;
    current = current.parent;
  }
  return false;
}

/**
 * If a GLB child mesh is selected, replace/remove should target that child only.
 */
function resolveScopedChildMesh(selectedObject, assetRoot) {
  if (!selectedObject || !assetRoot) return null;
  if (selectedObject === assetRoot) return null;
  if (!selectedObject.isMesh && !selectedObject.isSkinnedMesh) return null;
  if (!isUnderAsset(selectedObject, assetRoot)) return null;
  return selectedObject;
}

function resolveApplyTargetMeshes({
  selectedObject,
  assetRoot,
  selectedMaterial,
  scopedChildMesh = null,
}) {
  const child =
    resolveScopedChildMesh(selectedObject, assetRoot) || scopedChildMesh;
  if (child && isUnderAsset(child, assetRoot)) {
    return [child];
  }

  if (selectedMaterial?.meshes?.length) {
    return [...selectedMaterial.meshes];
  }

  const highlight =
    store.getState()?.assets_object?.materialHighlightMeshes || [];
  if (highlight.length > 0) {
    return highlight.filter((m) => m?.isMesh || m?.isSkinnedMesh);
  }

  return [findFirstMesh(assetRoot)].filter(Boolean);
}

function createFallbackMaterial() {
  return new THREE.MeshStandardMaterial({
    color: 0xffffff,
    metalness: 0,
    roughness: 0.5,
    name: 'Material',
  });
}

function meshUsesMaterial(mesh, material) {
  if (!mesh?.material || !material) return false;
  const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  return mats.some((m) => m === material || m?.uuid === material.uuid);
}

function assignMaterialToMesh(mesh, material) {
  if (!mesh || !material) return;
  mesh.material = material;
  mesh.userData = mesh.userData || {};
  mesh.userData.threeMaterialEdited = true;
}

/**
 * Unlink material from meshes but keep it in the asset material library list.
 * Does NOT delete the material from the material list.
 * Object gets a white material. Returns that white material for list selection.
 * @param {object[]} [meshSubset] - If set, only unlink from these meshes (e.g. selected child).
 */
function removeMaterialFromObject(assetRoot, materialItem, meshSubset = null) {
  if (!assetRoot || !materialItem?.material) {
    return { ok: false, whiteMaterial: null, meshes: [] };
  }
  const material = materialItem.material;
  const meshes =
    meshSubset?.length > 0
      ? meshSubset
      : materialItem.meshes?.length > 0
        ? materialItem.meshes
        : [];

  // Always keep this material in the list as an unused/dummy entry.
  storeMaterialInLibrary(assetRoot, material);

  // Object gets a white material after unlink.
  const whiteMaterial = createFallbackMaterial();
  const touchedMeshes = [];
  let activeMaterial = whiteMaterial;

  meshes.forEach((mesh) => {
    if (!meshUsesMaterial(mesh, material)) return;

    if (Array.isArray(mesh.material)) {
      const next = mesh.material.filter(
        (m) => m !== material && m?.uuid !== material.uuid
      );
      mesh.material = next.length > 0 ? next : [whiteMaterial];
      activeMaterial = Array.isArray(mesh.material)
        ? mesh.material[0] || whiteMaterial
        : mesh.material;
    } else {
      mesh.material = whiteMaterial;
      activeMaterial = whiteMaterial;
    }
    mesh.userData = mesh.userData || {};
    mesh.userData.threeMaterialEdited = true;
    syncChildMaterialOverridesToAssetRoot(mesh);
    touchedMeshes.push(mesh);
  });

  assetRoot.userData = assetRoot.userData || {};
  assetRoot.userData.threeMaterialEdited = true;
  return {
    ok: true,
    whiteMaterial: activeMaterial,
    meshes: touchedMeshes,
  };
}

/**
 * Assign a material onto target meshes (replace on object).
 * Previous materials stay in the list as unused so they can be reused.
 * The new material is removed from the unused library once assigned.
 */
function replaceMaterialOnObject(assetRoot, material, targetMeshes) {
  if (!assetRoot || !material || !targetMeshes?.length) return false;

  const previousMaterials = new Map();
  targetMeshes.forEach((mesh) => {
    if (!mesh?.isMesh && !mesh?.isSkinnedMesh) return;
    const mats = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];
    mats.forEach((mat) => {
      if (!mat?.uuid || mat === material || mat.uuid === material.uuid) return;
      previousMaterials.set(mat.uuid, mat);
    });
  });

  targetMeshes.forEach((mesh) => {
    if (!mesh?.isMesh && !mesh?.isSkinnedMesh) return;
    assignMaterialToMesh(mesh, material);
    syncChildMaterialOverridesToAssetRoot(mesh);
  });

  // Keep previous materials in the list even after unlink from these meshes.
  previousMaterials.forEach((prevMat) => {
    let stillUsed = false;
    assetRoot.traverse?.((obj) => {
      if (stillUsed) return;
      if (!obj.isMesh && !obj.isSkinnedMesh) return;
      if (meshUsesMaterial(obj, prevMat)) stillUsed = true;
    });
    if (!stillUsed) {
      storeMaterialInLibrary(assetRoot, prevMat);
    }
  });

  removeMaterialFromLibrary(assetRoot, material.uuid);
  assetRoot.userData = assetRoot.userData || {};
  assetRoot.userData.threeMaterialEdited = true;
  return true;
}

/**
 * Create a new material and add it to the list only (unused / not on any mesh).
 * Use Replace to assign it to an object later.
 */
function createLibraryMaterial({ assetRoot, existingMaterials }) {
  if (!assetRoot) return null;

  const material = new THREE.MeshStandardMaterial({
    color: 0x808080,
    metalness: 0,
    roughness: 0.5,
    name: nextMaterialName(existingMaterials),
  });
  material.userData = material.userData || {};
  material.userData.editorCreated = true;

  storeMaterialInLibrary(assetRoot, material);
  assetRoot.userData = assetRoot.userData || {};
  assetRoot.userData.threeMaterialEdited = true;

  return { material };
}

function selectMaterialForEdit({
  assetRoot,
  materialMeshes,
  setSelectedObjectState,
  syncSelectAll,
  preserveChildScope = false,
  onBeforeSelectRoot = null,
}) {
  if (!assetRoot) return;

  if (preserveChildScope && typeof onBeforeSelectRoot === 'function') {
    onBeforeSelectRoot();
  }

  setChildSelectionMode(null, { silent: true });
  clearMultiSelection();
  store.dispatch(setMaterialHighlightMeshes(materialMeshes || []));

  if (typeof window.__selectObjectInScene === 'function') {
    window.__selectObjectInScene(assetRoot, { keepMaterialHighlight: true });
  } else {
    store.dispatch(selected_object(assetRoot));
    setSelectedObjectState?.(assetRoot);
    syncSelectAll?.(assetRoot);
  }

  queueMicrotask(() => syncMaterialHighlightVisuals());
}

/**
 * Proxy mesh so unused library materials can still open the material editor.
 */
function getOrCreateLibraryEditProxy(assetRoot, material) {
  if (!assetRoot || !material) return null;
  assetRoot.userData = assetRoot.userData || {};
  let proxy = assetRoot.userData.__materialLibraryEditProxy;
  if (!proxy || !proxy.isMesh) {
    proxy = new THREE.Mesh(
      new THREE.SphereGeometry(0.01, 8, 8),
      material
    );
    proxy.name = '__MaterialLibraryEditProxy';
    proxy.visible = false;
    proxy.userData = { isMaterialLibraryProxy: true, nonSelectable: true };
    assetRoot.userData.__materialLibraryEditProxy = proxy;
  } else {
    proxy.material = material;
  }
  return proxy;
}

/**
 * Store Render View + apply viewport preview.
 * Material list spheres keep showing original GLB materials.
 * Syncs into Redux so publish payload always includes `renderView`.
 */
function setObjectRenderView(object, value) {
  if (!object) return;
  const next = normalizeRenderViewValue(value);
  object.renderView = next;
  object.userData = {
    ...(object.userData || {}),
    renderView: next,
    material_settings: {
      ...(object.userData?.material_settings || {}),
      renderView: next,
    },
  };
  applyRenderView(object, next);

  const id =
    object.name ||
    object._id ||
    object.url ||
    object.temp_file_name ||
    null;
  if (id) {
    store.dispatch(
      updateSceneMeshMaterialSettings({
        id,
        material_settings: { renderView: next },
      })
    );
  }
}

const GlbMaterialList = React.memo(({ objectt }) => {
  const { selectedObject, setSelectedObjectState, syncSelectAll } =
    objectt || {};
  const selectionId = getSelectionId(selectedObject);
  const assetRoot = useMemo(
    () => findMaterialListAssetRoot(selectedObject) || selectedObject,
    [selectedObject, selectionId]
  );

  const assetRootId = getSelectionId(assetRoot);
  const [renderView, setRenderView] = useState(() =>
    normalizeRenderViewValue(getObjectRenderView(assetRoot))
  );

  useEffect(() => {
    const value = normalizeRenderViewValue(getObjectRenderView(assetRoot));
    setRenderView(value);
    if (assetRoot) applyRenderView(assetRoot, value);
  }, [assetRoot, assetRootId]);

  const handleRenderViewChange = useCallback(
    (e) => {
      const raw = e?.target?.value;
      const value = normalizeRenderViewValue(
        raw === '' || raw == null ? DEFAULT_RENDER_VIEW : raw
      );
      setRenderView(value);
      setObjectRenderView(assetRoot, value);
    },
    [assetRoot]
  );

  const [listVersion, setListVersion] = useState(0);
  const [selectedMaterialId, setSelectedMaterialId] = useState(null);
  const [replacePickMode, setReplacePickMode] = useState(false);
  const [materialNameDraft, setMaterialNameDraft] = useState('');
  const replaceTargetMeshesRef = useRef([]);
  const scopedChildMeshRef = useRef(null);
  const skipChildScopeClearRef = useRef(false);

  const materials = useMemo(() => {
    if (!assetRoot) return [];
    return collectUniqueMaterialsInObject(assetRoot);
  }, [assetRoot, selectionId, listVersion]);

  // Remember selected child mesh so replace/remove apply to that child only.
  useEffect(() => {
    const child = resolveScopedChildMesh(selectedObject, assetRoot);
    if (child) {
      scopedChildMeshRef.current = child;
      return;
    }
    if (skipChildScopeClearRef.current) {
      skipChildScopeClearRef.current = false;
      return;
    }
    if (selectedObject === assetRoot) {
      scopedChildMeshRef.current = null;
    } else if (
      selectedObject &&
      assetRoot &&
      !isUnderAsset(selectedObject, assetRoot)
    ) {
      scopedChildMeshRef.current = null;
    }
  }, [selectedObject, assetRoot, selectionId]);

  const markPreserveChildScope = useCallback(() => {
    skipChildScopeClearRef.current = true;
  }, []);

  useEffect(() => {
    const fromScene = findMaterialIdForObject(materials, selectedObject);
    setSelectedMaterialId((prev) => {
      const prevItem = prev ? materials.find((m) => m.id === prev) : null;
      if (fromScene) return fromScene;
      if (prevItem) return prev;
      return null;
    });
  }, [materials, selectedObject, selectionId]);

  const selectedMaterial = useMemo(
    () => materials.find((m) => m.id === selectedMaterialId) || null,
    [materials, selectedMaterialId]
  );

  useEffect(() => {
    setMaterialNameDraft(selectedMaterial?.name || '');
  }, [selectedMaterial?.id, selectedMaterial?.name]);

  const handleMaterialNameChange = useCallback(
    (event) => {
      setMaterialNameDraft(event.target.value);
    },
    []
  );

  const commitMaterialName = useCallback(() => {
    if (!selectedMaterial?.material) return;
    const nextName = String(materialNameDraft || '').trim();
    const finalName = nextName || selectedMaterial.material.type || 'Material';
    if (selectedMaterial.material.name === finalName) {
      setMaterialNameDraft(finalName);
      return;
    }
    const before = captureMeshMaterialBindings(assetRoot);
    selectedMaterial.material.name = finalName;
    selectedMaterial.name = finalName;
    if (assetRoot) {
      assetRoot.userData = assetRoot.userData || {};
      assetRoot.userData.threeMaterialEdited = true;
    }
    const after = captureMeshMaterialBindings(assetRoot);
    undoRedoManager.recordSnapshot(
      new MeshMaterialBindingCommand(assetRoot, before, after, () => {
        setListVersion((v) => v + 1);
      })
    );
    setMaterialNameDraft(finalName);
    setListVersion((v) => v + 1);
  }, [selectedMaterial, materialNameDraft, assetRoot]);

  const recordBindingChange = useCallback(
    (mutateFn) => {
      if (!assetRoot) return null;
      const before = captureMeshMaterialBindings(assetRoot);
      const result = mutateFn();
      const after = captureMeshMaterialBindings(assetRoot);
      undoRedoManager.recordSnapshot(
        new MeshMaterialBindingCommand(assetRoot, before, after, () => {
          setListVersion((v) => v + 1);
        })
      );
      return result;
    },
    [assetRoot]
  );

  const getApplyTargets = useCallback(
    (materialItem) =>
      resolveApplyTargetMeshes({
        selectedObject,
        assetRoot,
        selectedMaterial: materialItem,
        scopedChildMesh: scopedChildMeshRef.current,
      }),
    [selectedObject, assetRoot]
  );

  const editorTarget = useMemo(() => {
    if (!selectedMaterial) return null;
    // Prefer the scoped child mesh for editing when a child is selected.
    const scoped = scopedChildMeshRef.current;
    if (
      scoped &&
      selectedMaterial.material &&
      meshUsesMaterial(scoped, selectedMaterial.material)
    ) {
      return scoped;
    }
    if (selectedMaterial.sampleMesh) return selectedMaterial.sampleMesh;
    if (selectedMaterial.isUnused && selectedMaterial.material) {
      return getOrCreateLibraryEditProxy(assetRoot, selectedMaterial.material);
    }
    return null;
  }, [selectedMaterial, assetRoot, listVersion, selectedObject]);

  const editorObjectt = useMemo(() => {
    if (!objectt || !editorTarget) return null;
    return { ...objectt, selectedObject: editorTarget };
  }, [objectt, editorTarget]);

  const handleSelectMaterial = (item) => {
    // Replace mode: clicking another material assigns it onto the target meshes.
    if (replacePickMode) {
      const targets = replaceTargetMeshesRef.current?.length
        ? replaceTargetMeshesRef.current
        : getApplyTargets(item);
      if (targets.length && item.material) {
        recordBindingChange(() =>
          replaceMaterialOnObject(assetRoot, item.material, targets)
        );
        setReplacePickMode(false);
        replaceTargetMeshesRef.current = [];
        setListVersion((v) => v + 1);
        setSelectedMaterialId(item.id);
        markPreserveChildScope();
        selectMaterialForEdit({
          assetRoot,
          materialMeshes: targets,
          setSelectedObjectState,
          syncSelectAll,
        });
        return;
      }
      setReplacePickMode(false);
    }

    setSelectedMaterialId(item.id);
    markPreserveChildScope();
    const highlightMeshes = scopedChildMeshRef.current
      ? [scopedChildMeshRef.current]
      : item.meshes?.length
        ? item.meshes
        : [];
    selectMaterialForEdit({
      assetRoot,
      materialMeshes: highlightMeshes,
      setSelectedObjectState,
      syncSelectAll,
    });
  };

  const handleAddMaterial = useCallback(() => {
    if (!assetRoot) return;

    const created = recordBindingChange(() =>
      createLibraryMaterial({
        assetRoot,
        existingMaterials: materials,
      })
    );
    if (!created) return;

    setListVersion((v) => v + 1);
    setSelectedMaterialId(created.material.uuid);
    setReplacePickMode(false);
    store.dispatch(setMaterialHighlightMeshes([]));
    queueMicrotask(() => syncMaterialHighlightVisuals());
  }, [assetRoot, materials, recordBindingChange]);

  const handleRemoveFromObject = useCallback(() => {
    if (!assetRoot || !selectedMaterial?.material) return;
    if (selectedMaterial.isUnused || selectedMaterial.meshCount === 0) return;

    const targets = getApplyTargets(selectedMaterial);
    const result = recordBindingChange(() =>
      removeMaterialFromObject(assetRoot, selectedMaterial, targets)
    );
    setReplacePickMode(false);
    setListVersion((v) => v + 1);

    // Select the white material now on the object in the material list.
    if (result?.ok && result.whiteMaterial?.uuid) {
      setSelectedMaterialId(result.whiteMaterial.uuid);
      markPreserveChildScope();
      selectMaterialForEdit({
        assetRoot,
        materialMeshes: result.meshes?.length ? result.meshes : targets,
        setSelectedObjectState,
        syncSelectAll,
      });
    } else {
      store.dispatch(setMaterialHighlightMeshes([]));
      queueMicrotask(() => syncMaterialHighlightVisuals());
    }
  }, [
    assetRoot,
    selectedMaterial,
    getApplyTargets,
    markPreserveChildScope,
    setSelectedObjectState,
    syncSelectAll,
    recordBindingChange,
  ]);

  const handleReplaceOnObject = useCallback(() => {
    if (!assetRoot || !selectedMaterial?.material) return;

    const targets = getApplyTargets(selectedMaterial);
    if (!targets.length) return;

    // Unused library material → apply it onto scoped child / object meshes.
    if (selectedMaterial.isUnused || selectedMaterial.meshCount === 0) {
      recordBindingChange(() =>
        replaceMaterialOnObject(assetRoot, selectedMaterial.material, targets)
      );
      setListVersion((v) => v + 1);
      setReplacePickMode(false);
      markPreserveChildScope();
      selectMaterialForEdit({
        assetRoot,
        materialMeshes: targets,
        setSelectedObjectState,
        syncSelectAll,
      });
      return;
    }

    // In-use material → pick another material from the list to replace on targets only.
    replaceTargetMeshesRef.current = targets;
    setReplacePickMode(true);
  }, [
    assetRoot,
    selectedMaterial,
    getApplyTargets,
    markPreserveChildScope,
    setSelectedObjectState,
    syncSelectAll,
    recordBindingChange,
  ]);

  if (!assetRoot) {
    return (
      <p className="text-xs text-gray-500 px-2 py-4">
        No materials found on this model.
      </p>
    );
  }

  return (
    <div className="text-xs flex flex-col gap-1 py-2 px-1">
      {/* Viewport preview for the object; material list stays on GLB materials. */}
      <GridDropdown
        label="Render View"
        value={renderViewDropdownValue(renderView)}
        onChange={handleRenderViewChange}
        options={RENDER_VIEW_OPTIONS}
      />

      <div className="flex items-center justify-between px-1 mb-2 mt-1">
        <p className="text-[10px] text-gray-400 font-medium">
          Materials in model
        </p>
        <span className="text-[9px] text-gray-500">{materials.length}</span>
      </div>

      {replacePickMode && (
        <p className="text-[9px] text-amber-400/90 px-1 mb-1">
          {scopedChildMeshRef.current
            ? 'Pick a material to apply on the selected child.'
            : 'Pick a material from the list to replace on the object.'}
        </p>
      )}

      <div className="glb-material-list-scroll grid grid-cols-4 max-h-[220px] overflow-y-auto mb-2 px-0.5">
        <button
          type="button"
          title="Add material to list (not applied to object)"
          aria-label="Add material to list"
          onClick={handleAddMaterial}
          className="flex flex-col items-center gap-1 px-1.5 py-1 bg-transparent text-[#2B2C2F] hover:text-[#45464a] transition-colors"
        >
          <span className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-full">
            <CirclePlus size={28} strokeWidth={1.5} />
          </span>
          <span className="w-full text-center text-[9px] leading-tight">
            Add material
          </span>
        </button>

          {materials.map((item) => {
            const isActive = item.id === selectedMaterialId;
            return (
              <button
                key={item.id}
                type="button"
                title={
                  item.isUnused
                    ? `${item.name} (unused)`
                    : replacePickMode
                      ? `Replace with ${item.name}`
                      : item.name
                }
                className="flex flex-col items-center gap-1.5 px-1.5 py-1 bg-transparent"
                onClick={() => handleSelectMaterial(item)}
              >
                <span
                  className={`inline-flex rounded-full p-[3px] transition-[box-shadow,outline] ${
                    isActive
                      ? 'outline outline-[3px] outline-[#55575C] outline-offset-0 shadow-[0_0_14px_rgba(255,255,255,0.55)]'
                      : replacePickMode
                        ? 'outline outline-1 outline-amber-500/60'
                        : 'outline-1 outline-dashed outline-gray-700/50'
                  }`}
                >
                  <MaterialSpherePreview
                    material={item.material}
                    size={28}
                    live={isActive}
                    className="!border-0"
                  />
                </span>
                <span
                  className={`w-full text-center text-[9px] leading-tight line-clamp-2 break-words ${
                    isActive ? 'text-white font-semibold' : 'text-gray-400'
                  }`}
                >
                  {item.name}
                  {item.isUnused ? (
                    <span className="block text-[8px] text-gray-500 font-normal">
                      unused
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
      </div>

      {selectedMaterial ? (
        <div className="border-t border-gray-800 pt-2 mt-1">
          <div className="flex items-center gap-2 px-1 mb-2">
            {selectedMaterial.material && (
              <MaterialSpherePreview
                material={selectedMaterial.material}
                size={56}
                live
                className="!border-gray-600"
              />
            )}
            <div className="min-w-0 flex-1">
              <input
                type="text"
                value={materialNameDraft}
                onChange={handleMaterialNameChange}
                onBlur={commitMaterialName}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.currentTarget.blur();
                  }
                  if (event.key === 'Escape') {
                    setMaterialNameDraft(selectedMaterial?.name || '');
                    event.currentTarget.blur();
                  }
                }}
                title="Material name"
                aria-label="Material name"
                className="w-full bg-[#18191C] rounded px-2 py-1 text-[10px] text-gray-100 font-medium outline-none"
              />
              {selectedMaterial.isUnused ? (
                <p className="text-[9px] text-gray-500 mt-0.5">
                  Unused — not on object
                </p>
              ) : selectedMaterial.meshCount > 1 ? (
                <p className="text-[9px] text-gray-500 mt-0.5">
                  Applies to {selectedMaterial.meshCount} meshes
                </p>
              ) : null}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                title={
                  selectedMaterial.isUnused
                    ? scopedChildMeshRef.current
                      ? 'Assign this material to the selected child'
                      : 'Assign this material to the object'
                    : scopedChildMeshRef.current
                      ? 'Replace material on selected child (pick from list)'
                      : 'Replace material on object (pick from list)'
                }
                aria-label="Replace material on object"
                onClick={handleReplaceOnObject}
                className={`w-6 h-6 flex items-center justify-center rounded transition-colors ${
                  replacePickMode
                    ? 'text-amber-300 bg-[#2a2418]'
                    : 'text-gray-300 hover:text-white hover:bg-[#2a2a2a]'
                }`}
              >
                <RefreshCw size={12} />
              </button>
              <button
                type="button"
                title="Remove material from object (keeps in list)"
                aria-label="Remove material from object"
                disabled={
                  selectedMaterial.isUnused || selectedMaterial.meshCount === 0
                }
                onClick={handleRemoveFromObject}
                className="w-6 h-6 flex items-center justify-center rounded text-gray-300 hover:text-amber-200 hover:bg-[#2a2a2a] disabled:opacity-40 disabled:pointer-events-none"
              >
                <Unlink size={12} />
              </button>
            </div>
          </div>
          {editorObjectt ? (
            <MaterialComponentParent
              key={`mat-editor-${selectedMaterialId}`}
              objectt={editorObjectt}
            />
          ) : null}
        </div>
      ) : (
        <p className="text-[10px] text-gray-500 px-2 py-3 border-t border-gray-800 mt-1">
          Select a material above to edit its properties.
        </p>
      )}
    </div>
  );
});

export default GlbMaterialList;
